import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useShop } from '@/hooks/useShop';
import ProductBrand from '@/components/feature/ProductBrand';

interface Props {
  children: React.ReactNode;
}

const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password', '/privacy'];

export default function AuthGuard({ children }: Props) {
  const { user, loading, isSuperAdmin } = useAuth();
  const { shop, loading: shopLoading } = useShop();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;

    const isPublicPage = PUBLIC_PATHS.includes(location.pathname);

    if (!user && !isPublicPage) {
      navigate('/login', { replace: true });
      return;
    }

    if (!user) return;

    // Chỉ redirect từ /login khi đã load xong VÀ user thực sự tồn tại
    if (location.pathname === '/login' && !loading) {
      if (shopLoading) return; // chờ biết super_admin có tiệm không để chọn đích
      const from = (location.state as { from?: string })?.from;
      navigate(from || (isSuperAdmin && !shop ? '/admin/shops' : '/'), { replace: true });
      return;
    }

    if (shopLoading || isPublicPage) return;

    // super_admin: có tiệm → dùng như chủ tiệm thường (+ vẫn vào được /admin/shops)
    // chưa có tiệm → chỉ được vào trang quản lý tiệm hoặc tự tạo tiệm
    if (isSuperAdmin) {
      if (!shop) {
        if (location.pathname !== '/admin/shops' && location.pathname !== '/setup-shop') {
          navigate('/admin/shops', { replace: true });
        }
        return;
      }
      if (location.pathname === '/setup-shop') {
        navigate('/', { replace: true });
      }
      return;
    }

    // User thường chưa có shop → ép qua màn thiết lập cửa hàng
    if (!shop && location.pathname !== '/setup-shop') {
      navigate('/setup-shop', { replace: true });
      return;
    }
    if (shop && location.pathname === '/setup-shop') {
      navigate('/', { replace: true });
    }
  }, [user, loading, shopLoading, shop, isSuperAdmin, navigate, location.pathname, location.state]);

  if (loading || (user && !isSuperAdmin && shopLoading)) {
    return (
      <div className="min-h-screen bg-[#f8f7f4] flex items-center justify-center">
        <div className="text-center">
          <ProductBrand compact />
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            <span>Đang tải...</span>
          </div>
        </div>
      </div>
    );
  }

  const isPublicPage = PUBLIC_PATHS.includes(location.pathname);

  if (!user && !isPublicPage) {
    return null;
  }

  // Chặn render trang trong khi chờ redirect setup-shop
  if (user && !isSuperAdmin && !isPublicPage) {
    if (!shop && location.pathname !== '/setup-shop') return null;
    if (shop && location.pathname === '/setup-shop') return null;
  }
  // super_admin chưa có tiệm: chỉ render trang quản lý tiệm / tạo tiệm
  if (user && isSuperAdmin && !isPublicPage && !shopLoading && !shop) {
    if (location.pathname !== '/admin/shops' && location.pathname !== '/setup-shop') return null;
  }

  return <>{children}</>;
}
